import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { GetDelegatedPedestrianRightsQuery, getDelegeeDetails, getPedestrianProducts, inviteDelegee, InvitePedestrianRightResponse, resendInvite, ResendInviteModel, updateDelegee, UpdatePedestrianRightDelegeeModel, UpdatePedestrianRightResponse } from "../../api/pedestrianRights.api";
import { ErrorDetailsModel } from "../../models/ErrorDetailsModel";
import { ForFleetManager } from "../../models/ForFleetManager";
import { InvitePedestrianRightRequestModel } from "../../models/InvitePedestrianRightRequestModel";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import { PedestrianProductModel } from "../../models/PedestrianProductModel";
import { DelegatedPedestrianRight, PedestrianDelegeesWithPagination } from "../../models/delegations/DelegatedPedestrianRight";
import { WithFiltersAndPaging } from "../../models/filters/WithFiltersAndPaging";
import { DeletePedestrianDelegeesModel } from "../../models/vehicle-assignments/DeletePedestrianDelegeesModel";
import { RevokePedestrianRightsModel } from "../../models/vehicle-assignments/RevokePedestrianRightsModel";
import { toastTriggers } from "../../ui/CustomToast/toastTriggers";
import { pedestrianTypes } from "../action-types/pedestrian.types";
import { failure, request, success } from "../helpers/action.helper";

export interface PedestrianRightResponse<Q> {
    query: Q;
};

export interface PedestrianSuccessResponse<T, Q> extends PedestrianRightResponse<Q> {
    totalPages: number;
    totalRecords: number;
    data: T[];
}

export const myProductsPedestrianRightsRequestAction =
    createAction<PedestrianRightResponse<GetDelegatedPedestrianRightsQuery>>(request(pedestrianTypes.GET_MY_PRODUCTS_PEDESTRIAN_RIGHTS));
export const myProductsPedestrianRightsSuccessAction =
    createAction<PedestrianSuccessResponse<DelegatedPedestrianRight, GetDelegatedPedestrianRightsQuery>>(success(pedestrianTypes.GET_MY_PRODUCTS_PEDESTRIAN_RIGHTS));
export const myProductsPedestrianRightsFailureAction =
    createAction<PedestrianRightResponse<GetDelegatedPedestrianRightsQuery>>(failure(pedestrianTypes.GET_MY_PRODUCTS_PEDESTRIAN_RIGHTS));

export const revokePedestrianRightsRequestAction = createAction(request(pedestrianTypes.REVOKE_PEDESTRIAN_RIGHTS));
export const revokePedestrianRightsSuccessAction = createAction<RevokePedestrianRightsModel>(success(pedestrianTypes.REVOKE_PEDESTRIAN_RIGHTS));
export const revokePedestrianRightsFailureAction = createAction(failure(pedestrianTypes.REVOKE_PEDESTRIAN_RIGHTS));

export const deletePedestrianDelegeesRequestAction = createAction(request(pedestrianTypes.DELETE_PEDESTRIAN_DELEGEES));
export const deletePedestrianDelegeesSuccessAction = createAction<DeletePedestrianDelegeesModel>(success(pedestrianTypes.DELETE_PEDESTRIAN_DELEGEES));
export const deletePedestrianDelegeesFailureAction = createAction(failure(pedestrianTypes.DELETE_PEDESTRIAN_DELEGEES));

export const toggleShowRevokePedestrianAction = createAction(pedestrianTypes.TOGGLE_SHOW_REVOKE_PEDESTRIAN);
export const toggleShowDeletePedestrianDelegeesAction = createAction(pedestrianTypes.TOGGLE_SHOW_DELETE_PEDESTRIAN_DELEGEES);

export const getPedestrianProductsThunk = createAsyncThunk<
    PedestrianProductModel[],
    { seasonTicketOwnerCrmId: string, language: string },
    { rejectValue: ErrorDetailsModel }
>(pedestrianTypes.GET_PEDESTRIAN_PRODUCTS, async (request, { rejectWithValue }) => {
    try {
        const res = await getPedestrianProducts(request.seasonTicketOwnerCrmId, request.language)
        return res.data;
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data as ErrorDetailsModel);
        }
        throw error;
    }
});

export const inviteActionThunk = createAsyncThunk<
    InvitePedestrianRightResponse,
    InvitePedestrianRightRequestModel,
    { rejectValue: ErrorDetailsModel }
>(pedestrianTypes.INVITE_DELEGEE, async (req, { rejectWithValue }) => {
    try {
        const res = await inviteDelegee(req);
        return res.data;
    } catch (error) {
        toastTriggers.error({
          content: {
            key: "parkingRights.inviteDelegee.error",
          },
        });
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data as ErrorDetailsModel);
        }
        throw error;
    }
});

export const getPedestrianDelegeesMyFleetRequestAction =
    createAction<WithFiltersAndPaging<ForFleetManager>>(request(pedestrianTypes.GET_PEDESTRIAN_DELEGEES_MYFLEET));
export const getPedestrianDelegeesMyFleetSuccessAction =
    createAction<PedestrianDelegeesWithPagination>(success(pedestrianTypes.GET_PEDESTRIAN_DELEGEES_MYFLEET));
export const getPedestrianDelegeesMyFleetFailureAction =
    createAction<AxiosError>(failure(pedestrianTypes.GET_PEDESTRIAN_DELEGEES_MYFLEET));

export const clearDelegeeDetailsAction = createAction(pedestrianTypes.CLEAR_DELEGEE_DETAILS);

export const disposeErrorAction = createAction(pedestrianTypes.DISPOSE_ERROR);

export const resendInviteActionThunk = createAsyncThunk(
    pedestrianTypes.RESEND_INVITE,
    async (req: ResendInviteModel) => {
        try {
            await resendInvite(req);

            toastTriggers.success({
                content: {
                    key: "parkingRights.resendInvite.success",
                },
            });
        }
        catch (error) {
            toastTriggers.error({
                content: {
                    key: "parkingRights.resendInvite.error",
                },
            });
            throw error;
        }
    }
);

export const getPedestrianDelegeeDetailsActionThunk = createAsyncThunk(
    pedestrianTypes.GET_PEDESTRIAN_DELEGEE,
    async (id: string) => {
        try {
            const res = await getDelegeeDetails(id);
            return res.data;
        }
        catch (error) {
            toastTriggers.error({
                content: {
                    key: "pedestrianRights.getDelegeeDetails.error",
                },
            });
            throw error;
        }
    },
);

export const updatePedestrianDelegeeActionThunk = createAsyncThunk<
    UpdatePedestrianRightResponse,
    UpdatePedestrianRightDelegeeModel,
    { rejectValue: ErrorDetailsModel }>(
        pedestrianTypes.UPDATE_DELEGEE,
        async (model: UpdatePedestrianRightDelegeeModel, { rejectWithValue }) => {
            try {
                const res = await updateDelegee(model);
                toastTriggers.success({
                    content: {
                        key: "parkingRights.updateDelegation.success",
                    },
                });
                return res.data;
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return rejectWithValue(error.response?.data as ErrorDetailsModel);
                }
                throw error;
            }
        }
    );